import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_MEET_WE_INFO_IMAGE_FILE_ACCEPT,
    APP_MEET_WE_INFO_IMAGE_FILE_ERROR_MESSAGE,
    applyAppMeetWeInfoDraft,
    clearAppMeetWeInfoDraft,
    createAppMeetWeInfoDraftPayload,
    isAppMeetWeInfoImageFile,
    NEW_APP_MEET_WE_INFO_DRAFT_KEY,
    readAppMeetWeInfoDraft,
    saveAppMeetWeInfoDraft,
} from './appMeetWeInfoDraftUtils'
import {
    APP_MEET_WE_INFO_IMAGE_LEAVE_MESSAGE,
    hasPendingAppMeetWeInfoImage,
    usePendingAppMeetWeInfoImageUnloadGuard,
} from './appMeetWeInfoLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PANEL_SX = {
    ...PANEL_SX,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
}

const IMAGE_PANEL_HEADER_SX = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
}

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = getFormChoiceCardSx

const MEET_WE_CATEGORY_OPTIONS = [
    {
        value: 0,
        title: 'GPS',
        description: '사용자 위치 기반으로 진행하는 우리끼리 대회를 안내합니다.',
    },
    {
        value: 1,
        title: 'QR 코드',
        description: 'QR 인증 방식으로 진행하는 우리끼리 대회를 안내합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

function MeetWeInfoImageUploadPanel({
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
    onClearClick,
}) {
    const fileInputRef = useRef(null)
    const imageFileName = getImageFileName(imageFile)
    const hasPendingFile = imageFile instanceof File

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClearClick()
    }

    return (
        <Paper
            variant="outlined"
            sx={IMAGE_PANEL_SX}
        >
            <Box sx={IMAGE_PANEL_HEADER_SX}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        안내 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        우리끼리 안내 화면에 노출되는 대표 이미지를 등록합니다.
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="우리끼리 안내 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    inputProps={{ accept: APP_MEET_WE_INFO_IMAGE_FILE_ACCEPT }}
                    inputRef={fileInputRef}
                    style={{ display: 'none' }}
                    name="image"
                    id="image"
                    type="file"
                    disabled={disabled}
                    onChange={onUploadClick}
                />
                <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        이미지 선택
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewAppMeetWeInfo() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '' }
    const listSearch = location.search || ''
    const [countryLoading, setCountryLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        image: '',
        continents: 'asia',
        cCode: '',
        category: 0, // 타입 - 0: gps, 1: qrcord
        title: '',
        content: '',
        url: '',
    })
    const [continents, setContinents] = useState('asia')
    const [countryArr, setCountryArr] = useState([])
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingAppMeetWeInfoImage(params) && !saving && !navigationUnlocked

    usePendingAppMeetWeInfoImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const baseParams = {
            image: '',
            continents: 'asia',
            cCode: '',
            category: 0,
            title: '',
            content: '',
            url: '',
        }
        const storedDraft = readAppMeetWeInfoDraft(NEW_APP_MEET_WE_INFO_DRAFT_KEY)
        const restoredDraft = applyAppMeetWeInfoDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppMeetWeInfoDraftPayload({
            params: baseParams,
        }))

        setContinents(restoredDraft.params.continents || 'asia')
        setParams((prev) => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    const getCountry = useCallback(async () => {
        setCountryLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCountry/getAllList', { continents: continents })
            if (data.code === 200) {
                setCountryArr(data.data)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('국가 목록을 불러오지 못했습니다.')
        } finally {
            setCountryLoading(false)
        }
    }, [continents, history])

    useEffect(() => {
        getCountry()
    }, [getCountry])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 우리끼리대회 안내 등록은 목록 query를 건드리지 않고 활성 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppMeetWeInfoDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppMeetWeInfoDraft(NEW_APP_MEET_WE_INFO_DRAFT_KEY)
            return
        }

        saveAppMeetWeInfoDraft(NEW_APP_MEET_WE_INFO_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAppMeetWeInfoDraft(NEW_APP_MEET_WE_INFO_DRAFT_KEY)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/appInfoMeetWes',
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                curPage: curPage,
                searchText: searchText,
                searchSelect: searchSelect
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearAppMeetWeInfoDraft(NEW_APP_MEET_WE_INFO_DRAFT_KEY)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_MEET_WE_INFO_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        const { name, value } = e.target

        if (name === 'continents') {
            setContinents(value)
            setParams(prevParams => ({
                ...prevParams,
                continents: value,
                cCode: ''
            }))
            return
        }

        setParams(prevParams => ({
            ...prevParams,
            [name]: value
        }))
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const { name, files } = e.target
        const file = files[0]

        if (!file) {
            return
        }

        if (!isAppMeetWeInfoImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(APP_MEET_WE_INFO_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        let reader = new FileReader()

        reader.onloadend = () => {
            setParams(prevParams => ({
                ...prevParams,
                [name]: file
            }))

            setImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const handleClearImage = () => {
        setParams(prevParams => ({
            ...prevParams,
            image: '',
        }))
        setImage('')
    }

    const handleCategoryChange = (category) => {
        setParams(prevParams => ({
            ...prevParams,
            category,
        }))
    }

    const createData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appMeetWeInfo/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 등록 후에도 이전 우리끼리대회 안내 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 우리끼리대회 안내를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        if (!params.image) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('이미지를 업로드해주세요.')
            return
        }

        if (!params.cCode) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('국가를 선택해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('image',params.image)
        formData.append('continents',params.continents)
        formData.append('cCode',params.cCode)
        formData.append('category',params.category)
        formData.append('title',params.title)
        formData.append('content',params.content)
        formData.append('url',params.url)

        createData(formData)
    }

    if (countryLoading && countryArr.length === 0) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 우리끼리대회 안내 등록</Title>
                <Prompt when={leaveGuardEnabled} message={APP_MEET_WE_INFO_IMAGE_LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={7}>
                            <MeetWeInfoImageUploadPanel
                                imageSrc={image}
                                imageFile={params.image}
                                disabled={saving}
                                onUploadClick={handleUploadClick}
                                onClearClick={handleClearImage}
                            />
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormSection
                                title="대상 설정"
                                description="안내 노출 대상 국가와 대회 방식을 함께 설정합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="대륙"
                                        select
                                        id="continents"
                                        name="continents"
                                        value={params.continents}
                                        size="small"
                                        disabled={saving || countryLoading}
                                        onChange={handleUpdateField}
                                    >
                                        <MenuItem value={'asia'}>아시아</MenuItem>
                                        <MenuItem value={'oceania'}>오세아니아</MenuItem>
                                        <MenuItem value={'n-america'}>북아메리카</MenuItem>
                                        <MenuItem value={'s-america'}>남아메리카</MenuItem>
                                        <MenuItem value={'europe'}>유럽</MenuItem>
                                        <MenuItem value={'africa'}>아프리카</MenuItem>
                                    </TextField>
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="국가"
                                        select
                                        id="cCode"
                                        name="cCode"
                                        value={params.cCode}
                                        size="small"
                                        disabled={saving || countryLoading}
                                        onChange={handleUpdateField}
                                    >
                                        {countryArr.map((row, index) => (
                                            <MenuItem key={index} value={row.cCode || ''}>{row.nameKr}</MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                                <FormControl fullWidth>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        대회종류
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 1,
                                            mt: 1,
                                        }}
                                    >
                                        {MEET_WE_CATEGORY_OPTIONS.map((option) => (
                                            <FormControlLabel
                                                key={option.value}
                                                value={option.value}
                                                control={<Radio checked={Number(params.category || 0) === option.value} onChange={() => handleCategoryChange(option.value)} />}
                                                disabled={saving}
                                                sx={getChoiceCardSx(Number(params.category || 0) === option.value)}
                                                label={(
                                                    <ChoiceCardLabel
                                                        title={option.title}
                                                        description={option.description}
                                                    />
                                                )}
                                            />
                                        ))}
                                    </Box>
                                </FormControl>
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appInfoMeetWes"
                        search={listSearch}
                        visible={true}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        onBackClick={handleBackClick}
                        submitDisabled={saving || countryLoading}
                        submitLabel={saving ? '저장 중...' : '저장'}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
